<template>
  <AModal modal-class="d-activity-report">
    <div class="overlay" />

    <div class="modal-content">
      <div class="modal-header">
        <h4>Report post</h4>
        <a
          class="button button-yellow button-icon button-close-white button-close-modal"
          @click="dismiss"
        />
      </div>

      <div
        class="modal-body loadable"
        :class="{ 'loading': (isLoading || isProcessing) }"
      >
        <div
          v-if="(isLoading || isProcessing)"
          class="loading-content"
        >
          <DuiLoadingIndicator size="lg" />
          <p
            v-if="isProcessing"
            class="space-top-2em"
          >
            Submitting report...
          </p>
        </div>

        <div class="loaded-content">
          <p class="space-bottom-2em">
            Please indicate a reason why you would like to report
            this post.
          </p>

          <form>
            <div class="form-group">
              <label for="parent-reason">Reason</label>

              <DuiDropdown
                v-model="selectedParentReason"
                name="parent-reason"
                :options="parentReasons"
                placeholder="Please select a reason"
              />
            </div>

            <div
              v-if="availableChildReasons"
              class="form-group"
            >
              <p>
                <strong>Please specify your reason</strong>
              </p>

              <DuiRadioGroup
                v-model="selectedChildReason"
                name="child-reason"
                :options="availableChildReasons"
              />
            </div>

            <div class="form-group">
              <label for="description">Further details</label>
              <textarea
                v-model="description"
                name="description"
                placeholder="Provide further details..."
              />
            </div>

            <div class="button-pair right">
              <button
                type="button"
                @click="dismiss"
              >
                Cancel
              </button>

              <button
                class="button-yellow"
                :disabled="!canSubmit"
                type="button"
                @click="submit"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AModal>
</template>

<script>
import Vue from 'vue';

import AModal from '@d-components/a-modal/a-modal.vue';
import activityFeedService from '@d-services/activity-feed-service/activity-feed-service';
import { getErrorMessage } from '@d-shared/utils/errors';

export default {
  components: {
    AModal,
  },
  props: {
    activity: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    childReasons: null,
    description: null,
    isLoading: false,
    isProcessing: false,
    parentReasons: null,
    selectedChildReason: null,
    selectedParentReason: null,
  }),
  computed: {
    availableChildReasons() {
      if (!this.selectedParentReason) {
        return null;
      }

      const displayedChildReasons = this.childReasons[this.selectedParentReason];

      if (!displayedChildReasons || !displayedChildReasons.length) return null;

      return displayedChildReasons;
    },

    canSubmit() {
      if (!this.selectedParentReason) return false;

      if (this.selectedParentReason && !this.availableChildReasons) return true;

      return !!(this.selectedParentReason && this.selectedChildReason);
    },
  },
  watch: {
    selectedParentReason: function() {
      this.$set(this, 'selectedChildReason', null);
    },
  },
  mounted() {
    this.loadReportReasons();
  },
  methods: {
    dismiss() {
      this.$emit('dismiss');
    },

    async loadReportReasons() {
      this.$set(this, 'isLoading', true);

      try {
        let reportReasons = await activityFeedService.getPostReportReasons();
        this.$set(this, 'parentReasons', reportReasons.firstLevel);
        this.$set(this, 'childReasons', reportReasons.secondLevel);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isLoading', false);
    },

    async submit() {
      if (!this.canSubmit) return;

      if (this.activity.is_reported === true) return;

      this.$set(this, 'isProcessing', true);

      try {
        await activityFeedService.reportPost(
          this.activity.slug,
          this.selectedParentReason,
          this.selectedChildReason,
          this.description,
        );
        this.$set(this.activity, 'is_reported', true);
        Vue.duiNotificationManager.success('Post has been reported.');
        this.dismiss();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },
  },
};
</script>
